import { useState } from 'react'
import { cn } from '#app/utils/misc.js'

/**
 * A number whose digits roll like a drum when they change: the new digit
 * drops in from above while the old one falls away (reads naturally for a
 * countdown). Purely visual — give the surrounding element an accessible
 * label, the digits are `aria-hidden`.
 */
export function Odometer({
	value,
	minDigits = 2,
	className,
}: {
	value: number
	minDigits?: number
	className?: string
}) {
	const text = String(Math.max(0, Math.floor(value))).padStart(minDigits, '0')
	return (
		<span
			aria-hidden="true"
			className={cn('inline-flex tabular-nums', className)}
		>
			{text.split('').map((digit, i) => (
				// key from the right so the ones digit stays the ones digit when
				// the number of digits changes (100 → 99)
				<OdometerDigit key={text.length - i} digit={digit} />
			))}
		</span>
	)
}

function OdometerDigit({ digit }: { digit: string }) {
	// derived state from the previous render: remember what we roll away from
	const [roll, setRoll] = useState({
		current: digit,
		previous: null as string | null,
		turns: 0,
	})
	if (roll.current !== digit) {
		setRoll({ current: digit, previous: roll.current, turns: roll.turns + 1 })
	}

	return (
		<span className="relative inline-block overflow-hidden">
			{/* sizer keeps the column width/height steady */}
			<span className="invisible">0</span>
			{roll.previous != null ? (
				<span
					key={`out-${roll.turns}`}
					className="absolute inset-0 animate-digit-out text-center motion-reduce:hidden"
				>
					{roll.previous}
				</span>
			) : null}
			<span
				key={`in-${roll.turns}`}
				className={cn(
					'absolute inset-0 text-center',
					roll.turns > 0 && 'animate-digit-in motion-reduce:animate-none',
				)}
			>
				{roll.current}
			</span>
		</span>
	)
}
